// frontend/src/auth/betaAdmissionToken.js
//
// BETA ADMISSION TOKEN  —  the carrier that survives the Clerk OAuth redirect.
//
// ============================================================================
// WHY THIS EXISTS
// ============================================================================
//
// Closed-beta signup proves the invite twice today:
//
//   1. `BetaKeyGate` prechecks the key (`POST auth/validate-beta-key/`, read-only)
//      before Clerk sign-up is allowed to mount, so an unadmitted visitor cannot
//      complete a whole Clerk signup and only THEN learn they were never admitted
//      — which would strand an orphaned Clerk identity with no Django user.
//   2. The bridge (`POST auth/clerk/`) is the authoritative admission point and
//      answers `403 beta_key_required` for an unmapped identity.
//
// For email/password signup the coordinator carries the key from (1) to (2) in
// memory (`pendingBetaKey`) and the user is prompted once. A Google/OAuth signup
// performs a full document navigation, which destroys that in-memory state, so
// those users were asked for the same key a second time.
//
// ============================================================================
// WHY sessionStorage IS PERMITTED FOR *THIS* VALUE — AND A TYPED KEY IS NOT
// ============================================================================
//
// The storage rules (`inviteKey.js`) allow exactly one credential-ish value in
// sessionStorage: a URL-derived SHARED (`GRUP_`) invite. A hand-typed beta key is
// explicitly banned from storage, and persisting the typed key to fix the double
// prompt was REJECTED. This module does not reverse that decision — it stores a
// materially different artifact, and the distinction is the whole justification:
//
//   * A BETA KEY is durable and reusable. A `SHARED` key stays live for the whole
//     event (`usage_limit` > 1), an `INDIVIDUAL` key is a standing bearer
//     credential for one person. Leaking one hands over real, repeatable admission.
//   * AN ADMISSION TOKEN is single-use, ~10 minutes old at most, server-invalidated
//     the instant it is redeemed, and worthless on its own: redeeming it also
//     requires a completed, Clerk-verified session (`Authorization: Bearer`), and
//     for an `INDIVIDUAL` key the backend still matches `target_email` against the
//     Clerk-verified email at redemption. Stealing it out of one tab's storage
//     buys an attacker nothing they could use without already being that user.
//
// So this is the SAME carve-out `inviteKey.js` documents, applied to a strictly
// weaker artifact — not a new exception to the rule. Crucially, issuing a token
// does NOT consume the key: the key is still spent exactly once, server-side,
// under lock, at redemption.
//
// The discipline that carve-out establishes is followed here in full:
//   * sessionStorage, NEVER localStorage — the token is scoped to the tab that
//     started this signup and must not attach itself to a different signup later
//     in the same browser.
//   * Never logged. Not the token, not a prefix of it. Console output leaks into
//     screen shares, bug-report screenshots, and session-replay capture.
//   * Explicitly cleared — on redemption, on failure, on sign-out, and on
//     abandoning signup (see the callers).
//
// ============================================================================
// EXPIRY IS ADVISORY HERE AND AUTHORITATIVE ON THE SERVER
// ============================================================================
//
// The stored expiry lets us avoid attaching a token we already know is dead (a
// slow OAuth round trip or a tab left open past the TTL). It is NOT a security
// control — the backend re-checks expiry at redemption and rejects with
// `beta_admission_token_invalid` regardless of what this file believes.
//
// Both clock-skew directions degrade safely: a fast client clock discards a
// still-valid token and the user sees the normal admission prompt; a slow one
// sends a dead token and the backend rejects it, which the coordinator turns into
// that same prompt. Neither is a dead end and neither can loop.

/** Tab-scoped home of the pending admission token for THIS signup attempt. */
export const ADMISSION_TOKEN_STORAGE_KEY = 'mythicBetaAdmissionToken';

/**
 * Treat a token as dead slightly before its stated expiry.
 *
 * The bridge request itself takes time, and a token that expires in flight is a
 * wasted round trip that lands the user on the admission prompt anyway. Spending
 * that margin locally makes the fallback immediate instead of delayed.
 */
const EXPIRY_SAFETY_MARGIN_MS = 5000;

function getSessionStorage() {
    if (typeof window === 'undefined' || !window.sessionStorage) {
        throw new Error('Beta admission token storage requires browser sessionStorage.');
    }
    return window.sessionStorage;
}

/**
 * Persist a freshly issued admission token for the rest of this signup attempt.
 *
 * Throws on malformed input rather than storing something unusable — a contract
 * drift on `admission_token` / `admission_token_expires_at` must surface loudly in
 * development, not silently reinstate the double prompt in production. Callers
 * that must not abort the signup on a capture failure (`BetaKeyGate`) catch this
 * and continue without a token; the admission prompt remains the fallback.
 */
export function storeAdmissionToken({ token, expiresAt, ttlSeconds } = {}) {
    if (typeof token !== 'string' || !token.trim()) {
        throw new Error(
            `storeAdmissionToken requires a non-empty token string; received ${typeof token}.`
        );
    }

    let expiresAtMs = null;
    if (expiresAt !== undefined && expiresAt !== null) {
        const parsed = Date.parse(expiresAt);
        if (Number.isNaN(parsed)) {
            // The value is a timestamp, not a secret, so it is safe to report.
            throw new Error(
                `storeAdmissionToken received an unparseable expiry: ${JSON.stringify(expiresAt)}.`
            );
        }
        expiresAtMs = parsed;
    } else if (Number.isInteger(ttlSeconds) && ttlSeconds > 0) {
        // Fallback only. `admission_token_expires_at` is the server's own clock and
        // is preferred; a TTL computed against the browser clock is second best.
        expiresAtMs = Date.now() + ttlSeconds * 1000;
    } else {
        throw new Error(
            'storeAdmissionToken requires admission_token_expires_at or a positive ' +
            'admission_token_ttl_seconds to know when the token dies.'
        );
    }

    getSessionStorage().setItem(
        ADMISSION_TOKEN_STORAGE_KEY,
        JSON.stringify({ token: token.trim(), expiresAtMs })
    );
}

/**
 * Read the response of `validateBetaKey()` and store the token it carried.
 *
 * Returns `true` when a token was captured. Returns `false` when the response
 * simply carried none — that is a legitimate state (an older backend, or a
 * `valid:false` precheck, which never mints one) and must not be treated as an
 * error: the user still reaches Clerk sign-up and still has the admission prompt.
 *
 * A token that IS present but unusable throws, via `storeAdmissionToken`.
 */
export function captureAdmissionTokenFromValidation(response) {
    const token = response?.admission_token;
    if (token === undefined || token === null) return false;

    storeAdmissionToken({
        token,
        expiresAt: response.admission_token_expires_at,
        ttlSeconds: response.admission_token_ttl_seconds
    });
    return true;
}

/**
 * The live admission token for this tab, or `null` when there isn't a usable one.
 *
 * Self-clearing: an expired or corrupt record is removed on read so it cannot be
 * reconsidered on the next attempt. `null` is an ordinary, fully-handled outcome —
 * every caller falls back to `ClerkBetaAdmissionScreen`, which is the authoritative
 * gate — so a missing token is not an error condition to raise on.
 */
export function readAdmissionToken() {
    const storage = getSessionStorage();
    const raw = storage.getItem(ADMISSION_TOKEN_STORAGE_KEY);
    if (!raw) return null;

    let record;
    try {
        record = JSON.parse(raw);
    } catch (error) {
        // Corruption we wrote ourselves, or a foreign write. Report that it
        // happened — never the contents — and fall back to the admission prompt.
        console.error(`betaAdmissionToken: stored record was not valid JSON (${error.message}); discarding it.`);
        storage.removeItem(ADMISSION_TOKEN_STORAGE_KEY);
        return null;
    }

    const hasShape = (
        record
        && typeof record.token === 'string'
        && record.token.trim()
        && Number.isFinite(record.expiresAtMs)
    );
    if (!hasShape) {
        console.error('betaAdmissionToken: stored record had an unexpected shape; discarding it.');
        storage.removeItem(ADMISSION_TOKEN_STORAGE_KEY);
        return null;
    }

    if (Date.now() >= record.expiresAtMs - EXPIRY_SAFETY_MARGIN_MS) {
        // Expected: a slow OAuth round trip or a tab left sitting. Not an error —
        // the user simply enters their key on the admission screen.
        storage.removeItem(ADMISSION_TOKEN_STORAGE_KEY);
        return null;
    }

    return record.token;
}

/**
 * Drop the token once it can no longer legitimately be spent.
 *
 * MUST be called on redemption, on a rejected/failed bridge attempt, on sign-out,
 * and when the user abandons sign-up. A single-use token left behind would attach
 * itself to a later, unrelated attempt in the same tab, where it can only produce
 * a confusing `beta_admission_token_invalid` for someone who never used it.
 */
export function clearAdmissionToken() {
    getSessionStorage().removeItem(ADMISSION_TOKEN_STORAGE_KEY);
}
